import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, type MeResponse } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type Step = 'password' | 'otp' | 'code';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const presetEmail = params.get('email') ?? '';
  const presetOtp = params.get('otp') ?? '';

  const presetErr = params.get('err') ?? '';

  const [step, setStep] = useState<Step>(presetEmail && presetOtp ? 'code' : 'otp');
  const [email, setEmail] = useState(presetEmail);
  const [otp, setOtp] = useState(presetOtp);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(presetErr ? humanError(new Error(presetErr)) : null);
  const [loading, setLoading] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());

  // The free-course door by default. An in-flight legacy magic link
  // (/login?email=&otp=) still resolves via the code form so links already
  // sitting in inboxes keep working.
  const legacyMagicLink = Boolean(presetEmail && presetOtp);

  // Pre-fill from legacy magic links (/login?email=&otp=) still in inboxes,
  // but never auto-submit — that previously fired twice and burned codes.
  // User has to press the button.

  // If the user is already fully authenticated, skip the login page. RequireAuth
  // protects the rest of the app; landing on /login while logged-in just
  // wastes a step and makes it look like the previous verify failed.
  //
  // Soft (instant /optin) sessions are the exception: they are "logged in" for
  // browsing the free course, but /login is exactly where they confirm their
  // email to upgrade to a full account — so don't bounce them away.
  useEffect(() => {
    let cancelled = false;
    api
      .get<MeResponse>('/api/auth/me')
      .then((res) => {
        if (!cancelled && res.user && !res.user.soft) navigate('/', { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function requestOtp() {
    if (!emailValid) return;
    setError(null);
    setLoading(true);
    try {
      await api.post('/api/auth/request-otp', { email: email.trim() });
      setStep('code');
    } catch (err) {
      setError(humanError(err));
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(em: string, code: string) {
    setError(null);
    setLoading(true);
    try {
      await api.post<{ ok: true; user: MeResponse['user'] }>('/api/auth/verify-otp', {
        email: em.trim(),
        otp: code,
      });
      await confirmSessionAndGo();
    } catch (err) {
      setError(humanError(err));
      setLoading(false);
    }
  }

  async function loginPassword() {
    if (!emailValid || password.length < 8) return;
    setError(null);
    setLoading(true);
    try {
      await api.post('/api/auth/login-password', { email: email.trim(), password });
      await confirmSessionAndGo();
    } catch (err) {
      setError(humanError(err));
      setLoading(false);
    }
  }

  // After a 2xx login, the server set a session cookie. Some browsers (in-app
  // webviews, strict tracking-prevention modes, certain private-mode configs)
  // accept the response but refuse to persist or send the cookie back. That
  // path used to silently bounce the user back to /login with no explanation,
  // which is what we saw in the Railway logs for the first failed attempt.
  // Confirm the session is real by reading /me; if it's not, surface the
  // cookie problem instead of looping.
  async function confirmSessionAndGo() {
    try {
      const me = await api.get<MeResponse>('/api/auth/me');
      if (!me.user) {
        setError(
          'Login hat geklappt, aber dein Browser blockiert anscheinend Cookies. ' +
            'Bitte deaktiviere den Tracking-Schutz für diese Seite oder öffne den Link in einem anderen Browser.',
        );
        setLoading(false);
        return;
      }
      qc.setQueryData(['me'], me);
      navigate('/', { replace: true });
    } catch (err) {
      setError(humanError(err));
      setLoading(false);
    }
  }

  const legalFooter = (
    <p className="pt-4 text-center text-xs text-muted-foreground">
      <a
        href="https://go.fussbusiness.de/impressum/"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline"
      >
        Impressum
      </a>
      <span className="mx-2">·</span>
      <a
        href="https://go.fussbusiness.de/datenschutz/"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline"
      >
        Datenschutz
      </a>
    </p>
  );

  return (
    <div className="mx-auto flex min-h-screen max-w-md items-center justify-center p-6">
      <Card className="w-full">
        <CardHeader>
          {!legacyMagicLink ? (
            <>
              <CardTitle>Zugang zum kostenlosen Fussbusiness Videokurs</CardTitle>
              <CardDescription>
                Gib deine E-Mail ein und leg direkt los — kostenlos und ohne Passwort.
              </CardDescription>
            </>
          ) : (
            <CardTitle>Anmelden</CardTitle>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Instant SOFT login for the free course — email only, no code.
              Mirrors the funnel /optin: the server sets a soft session cookie and
              302s into the free course. This is the path a logged-out visitor on a
              shared free-course link takes. Full-page navigation so the Set-Cookie
              + redirect take effect (it's a server GET, not an SPA route). */}
          {!legacyMagicLink && (
          <form
            className="space-y-3"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (emailValid) {
                window.location.assign(`/optin?email=${encodeURIComponent(email.trim())}`);
              }
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="soft-email">E-Mail-Adresse</Label>
              <Input
                id="soft-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="du@beispiel.de"
              />
            </div>
            <Button type="submit" disabled={!emailValid} className="w-full">
              Zum kostenlosen Kurs →
            </Button>
          </form>
          )}

          {legacyMagicLink && (
            <>
          {step === 'password' && (
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void loginPassword();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="du@beispiel.de"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !emailValid || password.length < 8}
                className="w-full"
              >
                Einloggen
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('otp');
                  setError(null);
                  setPassword('');
                }}
                className="w-full text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                Stattdessen Login-Code per E-Mail anfordern
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void requestOtp();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="email-otp">E-Mail-Adresse</Label>
                <Input
                  id="email-otp"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="du@beispiel.de"
                />
              </div>
              <Button type="submit" disabled={loading || !emailValid} className="w-full">
                Login-Code per E-Mail anfordern
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('password');
                  setError(null);
                }}
                className="w-full text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                Stattdessen mit Passwort einloggen
              </button>
            </form>
          )}

          {step === 'code' && (
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (otp.length === 6) void verifyOtp(email, otp);
              }}
            >
              <p className="text-sm text-muted-foreground">
                Wir haben dir einen 6-stelligen Code an <strong>{email}</strong> geschickt.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="otp">Login-Code</Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  autoFocus
                />
              </div>
              <Button type="submit" disabled={loading || otp.length !== 6} className="w-full">
                Einloggen
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('otp');
                  setOtp('');
                  setError(null);
                }}
                className="w-full text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                E-Mail ändern
              </button>
            </form>
          )}
            </>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          {legalFooter}
        </CardContent>
      </Card>
    </div>
  );
}

function humanError(err: unknown): string {
  const code = err instanceof Error ? err.message : String(err);
  switch (code) {
    case 'invalid_or_expired_otp': return 'Der Code ist ungültig oder abgelaufen.';
    case 'invalid_credentials': return 'E-Mail oder Passwort falsch.';
    case 'rate_limited': return 'Zu viele Versuche. Bitte kurz warten.';
    case 'not_authenticated': return 'Du bist nicht eingeloggt.';
    default: return 'Etwas ist schiefgelaufen. Bitte erneut versuchen.';
  }
}
